import type { ComboboxOption } from "@v1/components/designSystem/combobox/ComboboxContext";
import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { ProductRef } from "schemas/product";

import React from "react";

import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { LargeMenuItem } from "@v1/components/designSystem/menu/LargeMenuItem";
import { Menu } from "@v1/components/designSystem/menu/Menu";
import { useInfiniteStoreProducts } from "@v1/hooks/useInfiniteStoreProducts";
import {
  storeProductRequestLimits,
  useCachedProducts,
  useFetchStoreProducts,
  useStoreProducts,
} from "@v1/hooks/useStoreProducts";
import {
  selectDraftElementTemplateProducts,
  selectDraftElementType,
} from "@v1/reducers/core-reducer";
import { selectOpenPopoverId } from "@v1/reducers/ui-reducer";
import { useEditorSelector } from "@v1/store/store";
import { Loader } from "@v2/components/Loader";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import copy from "copy-to-clipboard";
import partition from "lodash-es/partition";
import startCase from "lodash-es/startCase";
import take from "lodash-es/take";
import uniqBy from "lodash-es/uniqBy";
import {
  Database as DatabaseIcon,
  Image as ImageIcon,
  Plus as PlusIcon,
  SearchIcon,
} from "lucide-react";
import { fakeProducts } from "replo-runtime/store/utils/fakeProducts";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

type SingleSelectProps = {
  isMultiselect?: false;
  value: ProductRef | null;
  onChange: (value: ProductRef | null) => void;
};

type MultiSelectProps = {
  isMultiselect: true;
  value: ProductRef[] | null;
  onChange: (value: ProductRef[]) => void;
};
type ProductSelectionComboboxProps = {
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  trigger?: React.ReactNode;
  triggerLayoutClassName?: string;
  size?: "sm" | "base";
  onRemove?: () => void;
  placeholderText?: string;
  allowDynamicData?: boolean;
  allowAutoOpen?: boolean;
  buttonEnhancerIconClassName?: string;
} & (SingleSelectProps | MultiSelectProps);

// Custom button component for product selection
type ProductSelectionButtonProps = {
  title: string;
  size?: "sm" | "base";
  isPlaceholder?: boolean;
  productImage?: string | null;
  layoutClassName?: string;
  onRemove?: () => void;
  allowDynamicData?: boolean;
  buttonEnhancerIconClassName?: string;
};

export const ProductSelectionButton = React.forwardRef<
  HTMLButtonElement,
  ProductSelectionButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(
  (
    {
      title,
      size = "base",
      isPlaceholder = false,
      productImage,
      layoutClassName,
      onRemove,
      allowDynamicData = true,
      buttonEnhancerIconClassName,
      ...props
    },
    ref,
  ) => {
    const buttonEnhancer = (() => {
      if (isPlaceholder) {
        if (allowDynamicData) {
          return (
            <div className="shrink-0">
              <DatabaseIcon
                size={size === "sm" ? 12 : 16}
                className="text-placeholder"
              />
            </div>
          );
        }
        return (
          <div className="shrink-0">
            <PlusIcon
              size={size === "sm" ? 12 : 16}
              className={twMerge(
                "text-placeholder",
                buttonEnhancerIconClassName,
              )}
            />
          </div>
        );
      }

      if (productImage) {
        return (
          <div
            className={twMerge(
              size === "sm" ? "h-4 w-4" : "h-5 w-5",
              "shrink-0 rounded-sm",
            )}
          >
            <img
              src={`${productImage}&width=40`}
              width={size === "sm" ? 16 : 20}
              height={size === "sm" ? 16 : 20}
              className="h-full w-full rounded-sm object-contain"
            />
          </div>
        );
      }

      // Default icon when product exists but has no image
      return (
        <div className="shrink-0">
          <ImageIcon size={16} className="text-placeholder" />
        </div>
      );
    })();

    return (
      <button
        ref={ref}
        className={twMerge(
          "border-component-border hover:text-default hover:bg-surface bg-sheet border-0.5 text-muted shadow-xs group flex w-full items-center justify-between rounded-md px-1.5 text-xs",
          !isPlaceholder && "border-dashed",
          size === "sm" ? "h-6" : "h-8",
          layoutClassName,
        )}
        type="button"
        data-testid="trigger-product"
        {...props}
      >
        {(buttonEnhancer || title) && (
          <div className="flex items-center gap-1 overflow-hidden">
            {buttonEnhancer}
            {title && (
              <span className="truncate text-xs font-medium">{title}</span>
            )}
          </div>
        )}
        {onRemove && (
          <FormFieldXButton
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          />
        )}
      </button>
    );
  },
);

ProductSelectionButton.displayName = "ProductSelectionButton";

const ProductSelectionComboboxLoader = ({
  isFetching,
  hasNextPage,
}: {
  isFetching: boolean;
  hasNextPage: boolean;
}) => {
  if (isFetching) {
    return (
      <div className="flex h-6 items-center justify-center">
        <Loader className="size-6" />
      </div>
    );
  }
  // NOTE: (Jackson, 2025-03-28) Smooth scrolling when loading is needed
  if (hasNextPage) {
    return <div className="h-6" />;
  }

  return null;
};

export const ProductSelectionCombobox = ({
  value,
  onChange,
  side = "bottom",
  sideOffset = 4,
  isMultiselect = false,
  trigger,
  triggerLayoutClassName,
  size = "base",
  onRemove,
  placeholderText,
  allowDynamicData = true,
  align = "start",
  allowAutoOpen = false,
  buttonEnhancerIconClassName,
}: ProductSelectionComboboxProps) => {
  const openPopoverId = useEditorSelector(selectOpenPopoverId);
  const isDefaultOpen = openPopoverId === "config-product-selector";
  const [isOpen, setIsOpen] = React.useState(allowAutoOpen && isDefaultOpen);
  const [searchTermInput, searchTerm, setSearchTerm] = useDebouncedState("");
  const { project } = useCurrentProjectContext();

  const elementType = useEditorSelector(selectDraftElementType);

  const { products: initialProducts } = useStoreProducts(
    "productsFromDraftElement",
  );

  const assignedTemplateProductIds = useEditorSelector(
    selectDraftElementTemplateProducts,
  );

  const { products: templateProducts } = useFetchStoreProducts({
    query: {
      type: "specificExternalIds",
      externalIds: assignedTemplateProductIds ?? [],
    },
    forceSkip:
      !assignedTemplateProductIds || assignedTemplateProductIds.length === 0,
  });

  const {
    products,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
  } = useInfiniteStoreProducts({
    pageSize:
      project?.hostingType === "replo" ||
      isFeatureEnabled("write-through-products-sync")
        ? storeProductRequestLimits.infiniteLoadingWriteThroughProductsSync
        : storeProductRequestLimits.infiniteLoadingShopifyAdminApi,
    searchTerm,
  });

  const { cachedProducts: cachedProductData } = useCachedProducts();

  // Check if we have real products or should use placeholders
  const hasRealProducts =
    templateProducts.length > 0 || products.length > 0 || isFetching;

  // Combine real products or use first 7 fake ones if no real products exist
  const productsData = (() => {
    if (searchTerm) {
      return uniqBy([...products], "id");
    }
    if (hasRealProducts) {
      return uniqBy(
        [...initialProducts, ...templateProducts, ...products],
        "id",
      );
    }
    return take(fakeProducts, 7);
  })();

  const options: ComboboxOption[] = React.useMemo(() => {
    // Grouping should only happen on product templates AND if there are template products
    const shouldApplyTemplateGrouping =
      elementType === "shopifyProductTemplate" && templateProducts.length > 0;

    const mappedOptions = productsData.map((product) => {
      const tagVariant: "success" | "info" | "neutral" = exhaustiveSwitch(
        product,
        "status",
      )({
        ACTIVE: "success",
        DRAFT: "info",
        ARCHIVED: "neutral",
      });

      const productImage = product.featured_image
        ? product.featured_image
        : product.images[0];

      const isAssigned =
        Array.isArray(assignedTemplateProductIds) &&
        assignedTemplateProductIds.some(
          (id) => String(id) === String(product.id),
        );

      let groupTitle: string | undefined = undefined;
      // Only assign group titles if we should be grouping
      if (shouldApplyTemplateGrouping) {
        if (isAssigned) {
          groupTitle = "Assigned to this Template";
        } else {
          groupTitle = "Unassigned";
        }
      }

      const menuItems: MenuItem[] = [
        {
          type: "leaf",
          id: `copy-product-id-${product.id}`,
          variant: "default",
          size: "sm",
          title: "Copy product ID",
          onSelect: () => {
            copy(String(product.id));
            successToast("Product ID copied", "");
          },
        },
        {
          type: "leaf",
          id: `copy-product-handle-${product.id}`,
          variant: "default",
          size: "sm",
          title: "Copy product handle",
          onSelect: () => {
            copy(product.handle);
            successToast("Product handle copied", "");
          },
        },
      ];

      return {
        value: String(product.id),
        label: (
          <Menu
            menuType="context"
            items={menuItems}
            triggerAsChild
            trigger={
              <div className="flex flex-row w-full">
                <LargeMenuItem
                  variant="product"
                  label={product.title}
                  description={product.handle}
                  productImage={
                    productImage ? `${productImage}&width=40` : undefined
                  }
                  tag={
                    <Tag size="sm" variant={tagVariant}>
                      {startCase(product.status)}
                    </Tag>
                  }
                  selected={
                    isMultiselect
                      ? (value as ProductRef[])?.some((item) => {
                          return String(item.productId) === String(product.id);
                        })
                      : String((value as ProductRef)?.productId) ===
                        String(product.id)
                  }
                  hasCheckbox={isMultiselect}
                  tooltipBehavior="always"
                  tooltip={
                    <div className="flex w-[250px] flex-col gap-1">
                      <p className="font-medium">{product.title}</p>
                      <p>Handle: {product.handle}</p>
                      <p>ID: {product.id}</p>
                    </div>
                  }
                />
              </div>
            }
          />
        ),
        displayValue: product.title,
        estimatedSize: 48,
        ...(groupTitle && { groupTitle }),
      };
    });

    if (shouldApplyTemplateGrouping) {
      // Partition the options into assigned and unassigned
      const [assignedOptions, unassignedOptions] = partition(
        mappedOptions,
        (option) => option.groupTitle === "Assigned to this Template",
      );
      // Concatenate them in the desired order
      return [...assignedOptions, ...unassignedOptions];
    }

    return mappedOptions;
  }, [
    productsData,
    value,
    isMultiselect,
    templateProducts,
    assignedTemplateProductIds,
    elementType,
  ]);

  // Internal combobox value is string | string[], so we need to convert between ProductRef and string value
  const selectedValue = React.useMemo(() => {
    if (isMultiselect) {
      return (
        (value as ProductRef[])?.map((item) => String(item.productId)) ?? []
      );
    }
    return (value as ProductRef)?.productId
      ? String((value as ProductRef).productId)
      : null;
  }, [value, isMultiselect]);

  // Find the selected product(s) to display appropriate title
  const getButtonTitle = () => {
    if (placeholderText !== undefined && !value) {
      return placeholderText;
    }
    if (isMultiselect) {
      const multiValue = value as ProductRef[] | null;
      if (!multiValue || multiValue.length === 0) {
        return "Connect Products";
      }
      const count = multiValue.length;
      return `${count} product${count !== 1 ? "s" : ""} connected`;
    } else {
      const singleValue = value as ProductRef | null;
      if (!singleValue) {
        return "Connect Product";
      }

      const product = productsData.find(
        (p) => String(p.id) === String(singleValue.productId),
      );
      return product?.title ?? "Connect Product";
    }
  };

  const getSelectedProductImage = (productId?: string) => {
    if (isMultiselect) {
      return null;
    }

    const product = productsData.find((p) => {
      return (
        String(p.id) === String(productId ?? (value as ProductRef)?.productId)
      );
    });

    const productImage = product?.featured_image
      ? product.featured_image
      : product?.images[0];

    return (productImage && `${productImage}&width=40`) || null;
  };

  const handleChange = (newValue: string | string[]) => {
    if (!newValue) {
      onChange(null as any);
      return;
    }

    if (isMultiselect) {
      const multiValues = newValue as string[];
      const productRefs = multiValues
        .map((id) => {
          const product = cachedProductData.find((p) => String(p.id) === id);
          if (!product) {
            return null;
          }
          return {
            productId: product.id,
            variantId: product.variants[0]?.id,
            title: product.title,
          } as ProductRef;
        })
        .filter(Boolean) as ProductRef[];

      (onChange as (value: ProductRef[] | null) => void)(productRefs);
    } else {
      const selectedProduct = productsData.find(
        (product) => String(product.id) === newValue,
      );
      if (selectedProduct) {
        const productRef: ProductRef = {
          productId: selectedProduct.id,
          variantId: selectedProduct.variants[0]?.id,
          title: selectedProduct.title,
        };
        (onChange as (value: ProductRef | null) => void)(productRef);
      }
    }
  };

  const handleSearch = (input: string) => {
    setSearchTerm(input);
  };

  const loadMoreProducts = async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  const shouldShowRemoveButton = onRemove && value;
  const comboboxTrigger = (
    <Combobox.Trigger>
      {trigger ?? (
        <ProductSelectionButton
          title={getButtonTitle()}
          isPlaceholder={!value}
          size={size}
          buttonEnhancerIconClassName={buttonEnhancerIconClassName}
          productImage={getSelectedProductImage()}
          onRemove={shouldShowRemoveButton ? onRemove : undefined}
          layoutClassName={triggerLayoutClassName}
          allowDynamicData={allowDynamicData}
        />
      )}
    </Combobox.Trigger>
  );
  const comboboxPopover = (
    <Combobox.Popover
      side={side}
      sideOffset={sideOffset}
      layoutClassName="w-[260px] h-[400px]"
      align={align}
    >
      <Combobox.Content
        title={isMultiselect ? "Connect Products" : "Connect Product"}
        inputPlaceholder="Search by product name, id, or SKU..."
        emptySearchMessage="No products found."
        onScrolledToEnd={
          hasRealProducts ? () => void loadMoreProducts() : undefined
        }
        loader={
          <ProductSelectionComboboxLoader
            isFetching={isFetching}
            hasNextPage={hasNextPage ?? false}
          />
        }
      >
        <Combobox.Input
          startEnhancer={<SearchIcon size={16} />}
          placeholder="Search by product name, id, or SKU..."
          size="sm"
          controlledInput={searchTermInput}
          controlledOnInputChange={handleSearch}
        />
      </Combobox.Content>
    </Combobox.Popover>
  );
  if (isMultiselect) {
    return (
      <Combobox.Root
        options={options}
        value={selectedValue as string[]}
        open={isOpen}
        onOpenChange={setIsOpen}
        onChange={handleChange}
        onInputChange={handleSearch}
        isMultiselect={true}
        layoutClassName="w-full flex-1"
      >
        {comboboxTrigger}
        {comboboxPopover}
      </Combobox.Root>
    );
  }

  return (
    <Combobox.Root
      options={options}
      open={isOpen}
      onOpenChange={setIsOpen}
      value={selectedValue as string}
      onChange={handleChange}
      layoutClassName="w-full flex-1"
      syncInputWithSelectedValue={false}
    >
      {comboboxTrigger}
      {comboboxPopover}
    </Combobox.Root>
  );
};
